import React, { useState, useRef, useEffect } from "react";
import api, { imgUrl, fallbackImg } from "@/lib/api";
import {
  Star,
  Check,
  ArrowRight,
  Mail,
  Phone,
  MapPin,
  Zap,
  Sparkles,
  Layers,
  Shield,
  Rocket,
  Heart,
  MessageCircle,
  X,
  Send,
  Instagram,
  Facebook,
  Twitter,
  Linkedin,
  Youtube,
  CalendarDays,
} from "lucide-react";

const ICONS = [Zap, Sparkles, Layers, Shield, Rocket, Heart];

function Img({ query, className, w, h }) {
  return (
    <img
      src={imgUrl(query, w, h)}
      style={{ backgroundColor: "#e5e7eb" }}
      onError={(e) => {
        const el = e.currentTarget;
        if (el.dataset.fb !== "1") {
          el.dataset.fb = "1";
          el.src = fallbackImg(query, w, h);
        }
      }}
      alt={query || ""}
      className={className}
      loading="lazy"
    />
  );
}

const fontStack = (font) => {
  const map = {
    "Cabinet Grotesk": '"Cabinet Grotesk", sans-serif',
    Poppins: '"Poppins", sans-serif',
    "Playfair Display": '"Playfair Display", serif',
    "Space Grotesk": '"Space Grotesk", sans-serif',
  };
  return map[font] || '"IBM Plex Sans", sans-serif';
};

/**
 * Renders a site schema into a real webpage.
 * onSelect(index) + selectedIndex enable edit-mode highlighting.
 */
export default function SiteRenderer({ schema, onSelect, selectedIndex, editable, projectId, sandbox }) {
  const integ = (schema?.meta?.integrations) || {};
  const showIntegrations = !editable;
  // Google Analytics (only on live/preview, never in the edit canvas)
  useEffect(() => {
    const ga = (integ.googleAnalytics || "").trim();
    if (!showIntegrations || !ga || window.__bfGA === ga) return;
    window.__bfGA = ga;
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${ga}`;
    document.head.appendChild(s);
    window.dataLayer = window.dataLayer || [];
    function gtag() { window.dataLayer.push(arguments); }
    window.gtag = gtag;
    gtag("js", new Date());
    gtag("config", ga);
  }, [integ.googleAnalytics, showIntegrations]);

  if (!schema || !schema.meta) return null;
  const { meta, sections = [] } = schema;
  const primary = meta.primaryColor || "#FF3B00";
  const accent = meta.accentColor || "#0055FF";
  const heading = fontStack(meta.font);

  const buyProduct = async (si, ii, it) => {
    if (editable) return;
    if (it.buyMode === "affiliate" && it.affiliateUrl) { window.open(it.affiliateUrl, "_blank", "noopener"); return; }
    if (!projectId) return;
    try {
      const { data } = await api.post(`/public/projects/${projectId}/product-checkout`, {
        section_index: si, item_index: ii, origin_url: window.location.origin,
      });
      if (data.url) window.location.href = data.url;
    } catch { window.alert("Checkout is unavailable right now."); }
  };

  const wrap = (idx, node) => (
    <div
      key={sections[idx].id || idx}
      id={`sec-${idx}`}
      onClick={editable ? (e) => { e.stopPropagation(); onSelect && onSelect(idx); } : undefined}
      className={`relative ${editable ? "cursor-pointer transition-all" : ""} ${
        editable && selectedIndex === idx ? "outline outline-2 outline-offset-[-2px]" : ""
      }`}
      style={{ scrollMarginTop: 72, ...(editable && selectedIndex === idx ? { outlineColor: primary } : {}) }}
    >
      {editable && selectedIndex === idx && (
        <span
          className="absolute top-0 left-0 z-20 px-2 py-0.5 text-[10px] font-tech uppercase tracking-widest text-white"
          style={{ background: primary }}
        >
          {sections[idx].type}
        </span>
      )}
      {node}
    </div>
  );

  const typeToIdx = {};
  sections.forEach((s, i) => { if (typeToIdx[s.type] === undefined) typeToIdx[s.type] = i; });
  const firstFooterIdx = sections.findIndex((x) => x.type === "footer");
  const resolveIdx = (label) => {
    const t = (label || "").toLowerCase();
    if (/\b(home|start|top)\b/.test(t)) return 0;
    const tests = [
      ["about", /about|story|who/],
      ["features", /feature|service|what|how/],
      ["pricing", /pric|plan/],
      ["products", /product|shop|store|menu|cart|buy/],
      ["gallery", /work|portfolio|gallery|project|photo/],
      ["testimonials", /testimonial|review|love|client/],
      ["contact", /contact|touch|reach|hire|book|reserv|email|location|visit/],
    ];
    for (const [type, re] of tests) if (re.test(t) && typeToIdx[type] !== undefined) return typeToIdx[type];
    if (/contact|touch|reach|hire|book|reserv|email|instagram|linkedin|dribbble|twitter/.test(t) && typeToIdx.footer !== undefined) return typeToIdx.footer;
    for (let i = 0; i < sections.length; i++) { const c = sections[i].content || {}; const tt = (c.title || c.heading || "").toLowerCase(); if (tt && t && tt.includes(t)) return i; }
    return null;
  };
  const go = (label) => {
    if (editable) return;
    const i = resolveIdx(label);
    if (i != null) document.getElementById(`sec-${i}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const goCta = () => {
    if (editable) return;
    for (const ty of ["pricing", "products", "contact", "cta", "footer"]) {
      if (typeToIdx[ty] !== undefined) { document.getElementById(`sec-${typeToIdx[ty]}`)?.scrollIntoView({ behavior: "smooth", block: "start" }); return; }
    }
  };

  return (
    <div className="bg-white text-[#0a2540] w-full" style={{ fontFamily: fontStack("body") }}>
      {sections.map((s, idx) => {
        const c = s.content || {};
        switch (s.type) {
          case "navbar":
            return wrap(idx, (
              <nav className="flex items-center justify-between px-8 py-5 border-b border-gray-100">
                {c.logoUrl || meta.logoUrl ? (
                  <img src={c.logoUrl || meta.logoUrl} alt={c.brand || meta.title} className="h-9 w-auto object-contain" />
                ) : (
                  <span className="text-xl font-bold" style={{ fontFamily: heading }}>{c.brand}</span>
                )}
                <div className="hidden md:flex items-center gap-8 text-sm text-gray-600">
                  {(c.links || []).map((l, i) => (
                    <span key={i} onClick={() => go(l)} className={`hover:text-black ${editable ? "" : "cursor-pointer"}`} data-testid={`nav-link-${i}`}>{l}</span>
                  ))}
                </div>
                {c.ctaText && (
                  <button onClick={goCta} className="text-sm font-semibold px-5 py-2.5 text-white" style={{ background: primary }} data-testid="nav-cta">{c.ctaText}</button>
                )}
              </nav>
            ));
          case "hero":
            return wrap(idx, (
              <header className="grid md:grid-cols-2 gap-10 items-center px-8 py-20 md:py-28 max-w-6xl mx-auto">
                <div>
                  {c.eyebrow && (<div className="text-xs font-tech uppercase tracking-[0.2em] mb-4" style={{ color: primary }}>{c.eyebrow}</div>)}
                  <h1 className="text-4xl md:text-6xl font-black leading-[1.05] tracking-tight" style={{ fontFamily: heading }}>{c.heading}</h1>
                  <p className="mt-6 text-lg text-gray-600 max-w-md">{c.subheading}</p>
                  <div className="mt-8 flex flex-wrap gap-4">
                    {c.ctaText && (<button onClick={goCta} className={`px-6 py-3.5 text-white font-semibold flex items-center gap-2 ${editable ? "" : "cursor-pointer"}`} style={{ background: primary }} data-testid="hero-cta">{c.ctaText} <ArrowRight size={18} /></button>)}
                    {c.secondaryCtaText && (<button onClick={() => go(c.secondaryCtaText)} className={`px-6 py-3.5 font-semibold border-2 border-[#0a2540] ${editable ? "" : "cursor-pointer"}`} data-testid="hero-cta-secondary">{c.secondaryCtaText}</button>)}
                  </div>
                </div>
                <div className="relative">
                  <div className="absolute -inset-3" style={{ background: accent, opacity: 0.12 }} />
                  <Img query={c.imageQuery} className="relative w-full h-72 md:h-96 object-cover" w={900} h={700} />
                </div>
              </header>
            ));
          case "features":
            return wrap(idx, (
              <section className="px-8 py-20 bg-[#faf9f6]">
                <div className="max-w-6xl mx-auto">
                  <div className="max-w-xl mb-12">
                    {c.subtitle && (<div className="text-xs font-tech uppercase tracking-[0.2em] mb-3" style={{ color: primary }}>{c.subtitle}</div>)}
                    <h2 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: heading }}>{c.title}</h2>
                  </div>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {(c.items || []).map((it, i) => {
                      const Ico = ICONS[i % ICONS.length];
                      return (
                        <div key={i} className="bg-white p-6 border border-gray-100">
                          <div className="w-11 h-11 flex items-center justify-center mb-4" style={{ background: `${primary}18`, color: primary }}><Ico size={20} /></div>
                          <h3 className="font-semibold text-lg mb-2" style={{ fontFamily: heading }}>{it.title}</h3>
                          <p className="text-sm text-gray-600 leading-relaxed">{it.description}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </section>
            ));
          case "about":
            return wrap(idx, (
              <section className="px-8 py-20 max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
                <Img query={c.imageQuery} className="w-full h-80 object-cover" w={800} h={800} />
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold mb-5" style={{ fontFamily: heading }}>{c.title}</h2>
                  <p className="text-gray-600 leading-relaxed text-lg">{c.text}</p>
                </div>
              </section>
            ));
          case "stats":
            return wrap(idx, (
              <section className="px-8 py-16" style={{ background: "#0a2540" }}>
                <div className="max-w-5xl mx-auto grid grid-cols-3 gap-8 text-center text-white">
                  {(c.items || []).map((it, i) => (
                    <div key={i}>
                      <div className="text-4xl md:text-5xl font-black" style={{ fontFamily: heading, color: i === 1 ? primary : "#fff" }}>{it.value}</div>
                      <div className="mt-2 text-sm text-white/60 font-tech uppercase tracking-wider">{it.label}</div>
                    </div>
                  ))}
                </div>
              </section>
            ));
          case "gallery":
            return wrap(idx, (
              <section className="px-8 py-20 max-w-6xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-10 text-center" style={{ fontFamily: heading }}>{c.title}</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {(c.imageQueries || []).map((q, i) => (
                    <Img key={i} query={q} className="w-full h-56 object-cover" w={600} h={600} />
                  ))}
                </div>
              </section>
            ));
          case "testimonials":
            return wrap(idx, (
              <section className="px-8 py-20 bg-[#faf9f6]">
                <div className="max-w-5xl mx-auto">
                  <h2 className="text-3xl md:text-4xl font-bold mb-10 text-center" style={{ fontFamily: heading }}>{c.title}</h2>
                  <div className="grid md:grid-cols-2 gap-6">
                    {(c.items || []).map((it, i) => (
                      <div key={i} className="bg-white p-8 border border-gray-100">
                        <div className="flex gap-1 mb-4" style={{ color: primary }}>{[0,1,2,3,4].map((k)=>(<Star key={k} size={16} fill="currentColor" />))}</div>
                        <p className="text-lg leading-relaxed">“{it.quote}”</p>
                        <div className="mt-5 text-sm"><span className="font-semibold">{it.name}</span><span className="text-gray-500"> — {it.role}</span></div>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            ));
          case "pricing":
            return wrap(idx, (
              <section className="px-8 py-20 max-w-5xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center" style={{ fontFamily: heading }}>{c.title}</h2>
                <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
                  {(c.plans || []).map((p, i) => (
                    <div key={i} className="border p-8" style={{ borderColor: i === 1 ? primary : "#e5e7eb", borderWidth: i === 1 ? 2 : 1 }}>
                      <div className="font-tech uppercase text-xs tracking-widest text-gray-500">{p.name}</div>
                      <div className="mt-3 mb-6"><span className="text-4xl font-black" style={{ fontFamily: heading }}>{p.price}</span><span className="text-gray-500">{p.period}</span></div>
                      <ul className="space-y-3 mb-8">
                        {(p.features || []).map((f, k) => (<li key={k} className="flex items-center gap-2 text-sm"><Check size={16} style={{ color: primary }} /> {f}</li>))}
                      </ul>
                      <button className="w-full py-3 font-semibold text-white" style={{ background: i === 1 ? primary : "#0a2540" }}>{p.ctaText}</button>
                    </div>
                  ))}
                </div>
              </section>
            ));
          case "products":
            return wrap(idx, (
              <section className="px-8 py-20 bg-[#faf9f6]">
                <div className="max-w-6xl mx-auto">
                  <div className="text-center mb-12">
                    <h2 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: heading }}>{c.title}</h2>
                    {c.subtitle && <p className="mt-3 text-gray-600">{c.subtitle}</p>}
                  </div>
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-6">
                    {(c.items || []).map((it, i) => (
                      <div key={i} className="bg-white border border-gray-100 group">
                        <div className="h-52 overflow-hidden">
                          <Img query={it.imageQuery} className="w-full h-full object-cover group-hover:scale-105 transition-transform" w={500} h={500} />
                        </div>
                        <div className="p-4">
                          <div className="flex items-start justify-between gap-2">
                            <h3 className="font-semibold" style={{ fontFamily: heading }}>{it.name}</h3>
                            <span className="font-bold whitespace-nowrap" style={{ color: primary }}>{c.currency || "$"}{it.price}</span>
                          </div>
                          {it.description && <p className="text-sm text-gray-500 mt-1">{it.description}</p>}
                          {!editable && it.buyMode === "affiliate" && it.affiliateUrl ? (
                            <a href={it.affiliateUrl} target="_blank" rel="noopener sponsored" className="mt-4 block text-center w-full py-2.5 text-sm font-semibold text-white cursor-pointer" style={{ background: primary }} data-testid={`product-buy-${idx}-${i}`}>{it.ctaText || "Add to cart"}</a>
                          ) : (
                            <button onClick={() => buyProduct(idx, i, it)} className={`mt-4 w-full py-2.5 text-sm font-semibold text-white ${editable ? "" : "cursor-pointer"}`} style={{ background: primary }} data-testid={`product-buy-${idx}-${i}`}>{it.ctaText || "Add to cart"}</button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            ));
          case "cta":
            return wrap(idx, (
              <section className="px-8 py-24 text-center text-white" style={{ background: primary }}>
                <h2 className="text-3xl md:text-5xl font-black max-w-2xl mx-auto" style={{ fontFamily: heading }}>{c.heading}</h2>
                <p className="mt-4 text-white/85 max-w-lg mx-auto text-lg">{c.subheading}</p>
                {c.ctaText && (<button onClick={goCta} className={`mt-8 bg-white px-8 py-3.5 font-semibold ${editable ? "" : "cursor-pointer"}`} style={{ color: primary }} data-testid="cta-section-btn">{c.ctaText}</button>)}
              </section>
            ));
          case "contact":
            return wrap(idx, (
              <section className="px-8 py-20 max-w-4xl mx-auto text-center">
                <h2 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: heading }}>{c.title}</h2>
                {c.subtitle && (<p className="mt-3 text-gray-600 text-lg">{c.subtitle}</p>)}
                <div className="mt-10 flex flex-wrap justify-center gap-8 text-sm">
                  {c.email && (<span className="flex items-center gap-2"><Mail size={16} style={{ color: primary }} /> {c.email}</span>)}
                  {c.phone && (<span className="flex items-center gap-2"><Phone size={16} style={{ color: primary }} /> {c.phone}</span>)}
                  {c.address && (<span className="flex items-center gap-2"><MapPin size={16} style={{ color: primary }} /> {c.address}</span>)}
                </div>
              </section>
            ));
          case "custom":
            return wrap(idx, <CustomBlock html={c.html || ""} />);
          case "footer":
            return (
              <React.Fragment key={sections[idx].id || idx}>
                {showIntegrations && idx === firstFooterIdx && <ConnectStrip integ={integ} primary={primary} heading={heading} />}
                {wrap(idx, (
              <footer className="px-8 py-12" style={{ background: "#0a2540", color: "#fff" }}>
                <div className="max-w-6xl mx-auto flex flex-col md:flex-row md:items-center justify-between gap-6">
                  <div>
                    <div className="text-lg font-bold" style={{ fontFamily: heading }}>{c.brand}</div>
                    <p className="text-white/50 text-sm mt-1">{c.text}</p>
                  </div>
                  <div className="flex gap-6 text-sm text-white/70">
                    {(c.links || []).map((l, i) => (<span key={i} onClick={() => go(l)} className={editable ? "" : "cursor-pointer hover:text-white"} data-testid={`footer-link-${i}`}>{l}</span>))}
                  </div>
                </div>
              </footer>
                ))}
              </React.Fragment>
            );
          default:
            return null;
        }
      })}
      {showIntegrations && firstFooterIdx < 0 && <ConnectStrip integ={integ} primary={primary} heading={heading} />}
      {showIntegrations && <IntegrationsFloating integ={integ} primary={primary} sandbox={sandbox} />}
      {projectId && meta.chatbot?.enabled !== false && (
        <SiteAssistant projectId={projectId} meta={meta} primary={primary} sandbox={sandbox} />
      )}
    </div>
  );
}

function normMapsEmbed(val) {
  const v = (val || "").trim();
  if (!v) return "";
  if (v.startsWith("http")) return v;
  return `https://www.google.com/maps?q=${encodeURIComponent(v)}&output=embed`;
}

const SOCIAL_DEFS = [
  ["instagram", Instagram],
  ["facebook", Facebook],
  ["twitter", Twitter],
  ["linkedin", Linkedin],
  ["youtube", Youtube],
];

function ConnectStrip({ integ, primary, heading }) {
  const socials = SOCIAL_DEFS.filter(([k]) => (integ[k] || "").trim());
  const mc = (integ.mailchimp || "").trim();
  const mapUrl = normMapsEmbed(integ.googleMaps || "");
  if (!mc && socials.length === 0 && !mapUrl) return null;
  return (
    <section data-testid="integrations-strip" className="px-8 py-14 text-white" style={{ background: "#0e1b2e" }}>
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap gap-10 items-start">
          {mc && (
            <div className="min-w-[240px] flex-1">
              <div className="font-bold text-lg mb-3" style={{ fontFamily: heading }}>Join our newsletter</div>
              <form action={mc} method="post" target="_blank" className="flex gap-2 flex-wrap">
                <input type="email" name="EMAIL" required placeholder="you@email.com" className="flex-1 min-w-[160px] rounded-lg px-3 py-2.5 text-sm text-gray-900 outline-none" data-testid="newsletter-email" />
                <button type="submit" data-testid="newsletter-submit" className="rounded-lg text-white font-semibold px-5 py-2.5 text-sm" style={{ background: primary }}>Subscribe</button>
              </form>
            </div>
          )}
          {socials.length > 0 && (
            <div className="min-w-[200px]">
              <div className="font-bold text-lg mb-3" style={{ fontFamily: heading }}>Follow us</div>
              <div className="flex gap-2.5 flex-wrap">
                {socials.map(([k, Icon]) => (
                  <a key={k} href={integ[k]} target="_blank" rel="noopener noreferrer" title={k} data-testid={`social-${k}`} className="w-10 h-10 rounded-full flex items-center justify-center hover:scale-105 transition-transform" style={{ background: "rgba(255,255,255,.12)" }}>
                    <Icon size={18} />
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
        {mapUrl && (
          <div className="mt-7">
            <iframe title="map" src={mapUrl} className="w-full h-72 border-0" loading="lazy" referrerPolicy="no-referrer-when-downgrade" data-testid="integrations-map" />
          </div>
        )}
      </div>
    </section>
  );
}

function IntegrationsFloating({ integ, primary, sandbox }) {
  const pos = sandbox ? "absolute" : "fixed";
  const wa = (integ.whatsapp || "").replace(/[^0-9]/g, "");
  const cal = (integ.calendly || "").trim();
  const waHref = wa ? `https://wa.me/${wa}${integ.whatsappMessage ? `?text=${encodeURIComponent(integ.whatsappMessage)}` : ""}` : "";
  return (
    <>
      {cal && (
        <button
          data-testid="calendly-btn"
          onClick={() => window.open(cal, "_blank", "noopener")}
          className={`${pos} left-4 flex items-center gap-1.5 text-white text-sm font-semibold rounded-full px-4 py-2.5 shadow-xl z-[9998] hover:scale-105 transition-transform`}
          style={{ background: primary, bottom: sandbox ? 138 : 142 }}
        >
          <CalendarDays size={16} /> Book a call
        </button>
      )}
      {waHref && (
        <a
          data-testid="whatsapp-btn"
          href={waHref}
          target="_blank"
          rel="noopener noreferrer"
          className={`${pos} left-4 w-13 h-13 rounded-full flex items-center justify-center shadow-xl z-[9998] hover:scale-105 transition-transform`}
          style={{ background: "#25D366", bottom: 80, width: 52, height: 52 }}
          title="Chat on WhatsApp"
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="#fff"><path d="M12 2a10 10 0 0 0-8.6 15l-1.4 5 5.1-1.3A10 10 0 1 0 12 2zm5.8 14.2c-.2.7-1.4 1.3-2 1.4-.5.1-1.1.1-1.8-.1-.4-.1-1-.3-1.6-.6-2.9-1.2-4.7-4.1-4.9-4.3-.1-.2-1.1-1.5-1.1-2.8s.7-2 .9-2.3c.2-.3.5-.3.7-.3h.5c.2 0 .4 0 .6.5s.8 1.9.8 2c.1.1.1.3 0 .5s-.2.4-.3.5l-.4.5c-.1.1-.3.3-.1.6s.6 1 1.3 1.7c.9.8 1.6 1 1.9 1.2.3.1.4.1.6-.1s.7-.8.9-1.1.4-.2.6-.1 1.5.7 1.7.9.4.2.5.3 0 .6-.1 1.1z"/></svg>
        </a>
      )}
    </>
  );
}

function CustomBlock({ html }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.innerHTML = html || "";
    // re-execute any <script> tags (innerHTML-injected scripts don't run)
    el.querySelectorAll("script").forEach((old) => {
      const s = document.createElement("script");
      for (const a of old.attributes) s.setAttribute(a.name, a.value);
      s.textContent = old.textContent;
      old.replaceWith(s);
    });
  }, [html]);
  return <div ref={ref} data-testid="custom-code-block" className="w-full" />;
}

function SiteAssistant({ projectId, meta, primary, sandbox }) {
  const cfg = meta.chatbot || {};
  const greeting = cfg.greeting || "Hi! 👋 How can I help you today?";
  const botName = cfg.name || "Assistant";
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [showLead, setShowLead] = useState(false);
  const [lead, setLead] = useState({ name: "", email: "", note: "" });
  const [leadBusy, setLeadBusy] = useState(false);
  const [leadDone, setLeadDone] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (open) setMessages((m) => (m.length <= 1 ? [{ role: "assistant", content: greeting }] : m));
  }, [open, greeting]); // eslint-disable-line
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, busy]);

  const send = async () => {
    const t = input.trim();
    if (!t || busy) return;
    setInput("");
    const history = messages;
    setMessages((m) => [...m, { role: "user", content: t }]);
    setBusy(true);
    try {
      const { data } = await api.post(`/public/projects/${projectId}/assistant`, { message: t, history });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry, I couldn't connect right now." }]);
    } finally {
      setBusy(false);
    }
  };

  const posBase = sandbox ? "absolute" : "fixed";

  const submitLead = async () => {
    if (!lead.name.trim() || !lead.email.includes("@") || leadBusy) return;
    setLeadBusy(true);
    try {
      await api.post(`/public/projects/${projectId}/lead`, lead);
      setLeadDone(true);
      setMessages((m) => [...m, { role: "assistant", content: `Thanks ${lead.name.split(" ")[0]}! We've got your details and will be in touch soon. 🎉` }]);
      setTimeout(() => { setShowLead(false); setLeadDone(false); setLead({ name: "", email: "", note: "" }); }, 1800);
    } catch { /* ignore */ } finally {
      setLeadBusy(false);
    }
  };

  return (
    <>
      {open && (
        <div className={`${posBase} bottom-24 right-6 w-[340px] max-w-[90vw] h-[460px] max-h-[70vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden z-[9999] border border-black/5`} data-testid="site-assistant-panel">
          <div className="flex items-center justify-between px-4 py-3 text-white font-semibold" style={{ background: primary }}>
            <span className="flex items-center gap-2"><MessageCircle size={17} /> {botName}</span>
            <button onClick={() => setOpen(false)} className="opacity-90 hover:opacity-100"><X size={18} /></button>
          </div>
          <button
            data-testid="lead-capture-toggle"
            onClick={() => setShowLead((v) => !v)}
            className="flex items-center justify-center gap-1.5 py-1.5 text-xs font-medium border-b border-gray-100 hover:bg-gray-50 transition-colors"
            style={{ color: primary }}
          >
            <Mail size={13} /> {showLead ? "Back to chat" : "Leave your details"}
          </button>
          {showLead ? (
            <div className="flex-1 overflow-auto p-4 bg-white flex flex-col gap-3" data-testid="lead-capture-form">
              <p className="text-sm text-gray-600">Share your details and we'll follow up with you.</p>
              <input data-testid="lead-name" value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} placeholder="Your name" className="border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-gray-400" />
              <input data-testid="lead-email" type="email" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} placeholder="you@email.com" className="border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-gray-400" />
              <textarea data-testid="lead-note" value={lead.note} onChange={(e) => setLead({ ...lead, note: e.target.value })} placeholder="What are you interested in? (optional)" rows={2} className="border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-gray-400 resize-none" />
              <button data-testid="lead-submit" onClick={submitLead} disabled={leadBusy || leadDone} className="mt-1 py-2.5 rounded-lg text-white text-sm font-semibold disabled:opacity-60" style={{ background: primary }}>
                {leadDone ? "✓ Sent!" : leadBusy ? "Sending…" : "Send my details"}
              </button>
            </div>
          ) : (
          <div className="flex-1 overflow-auto p-3 bg-[#f7f7f8] space-y-2">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm leading-snug ${m.role === "user" ? "text-white" : "bg-white text-gray-800 border border-gray-100"}`} style={m.role === "user" ? { background: primary } : undefined}>
                  {m.content}
                </div>
              </div>
            ))}
            {busy && <div className="text-gray-400 text-xs px-2">typing…</div>}
            <div ref={endRef} />
          </div>
          )}
          {!showLead && (
          <div className="flex border-t border-gray-100">
            <input data-testid="site-assistant-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Type a message…" className="flex-1 px-4 py-3 text-sm outline-none" />
            <button data-testid="site-assistant-send" onClick={send} disabled={busy} className="px-4 text-white disabled:opacity-60" style={{ background: primary }}><Send size={16} /></button>
          </div>
          )}
        </div>
      )}
      <button
        data-testid="site-assistant-toggle"
        onClick={() => setOpen((o) => !o)}
        className={`${posBase} bottom-6 right-6 w-14 h-14 rounded-full text-white flex items-center justify-center shadow-2xl z-[9999] hover:scale-105 transition-transform`}
        style={{ background: primary }}
        aria-label="Chat with us"
      >
        {open ? <X size={24} /> : <MessageCircle size={24} />}
      </button>
    </>
  );
}
